/**
 * Call-Off Detail Tab — left column of read-only field cards,
 * right column showing the audit history timeline.
 *
 * Mirrors the layout used by POLineDetailTab so the modal feels
 * consistent across PO and Call-Off surfaces.
 */
import { Calendar, User, Package, Truck, Warehouse, ArrowUpDown, FileText } from 'lucide-react';
import { describeTotal, palletTotal } from './callOffTotals';
import type {
  CallOff, CallOffTimelineEvent, CallOffEditHistoryEntry,
  EditHistoryItem, EditHistoryChangedItem,
} from '../../../lib/api';
import VerticalTimeline, { TimelineItem } from '../../purchasing/timeline/VerticalTimeline';
import { Progress } from '../../ui/progress';
import CallOffHaulierField from './CallOffHaulierField';
import CallOffHaulagePanel from './CallOffHaulagePanel';
import CallOffCustomerHaulageCost from './CallOffCustomerHaulageCost';
import CallOffStatusBadges from '../CallOffStatusBadges';
import {
  CUSTOMER_HAULAGE_LABELS, customerCostAction, haulageEntryLine, haulierChangeAction,
  haulierPreviousEntries,
} from './haulierAudit';

// CR-7781: lifecycle events whose name alone would mislead. "Confirmed" is the
// supplier's confirmation; the haulier's is its own history entry.
const TIMELINE_EVENT_LABELS: Record<string, string> = {
  confirmed: 'Supplier Confirmed',
};

const DESTINATION_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  warehouse: Warehouse,
  direct: Truck,
  mixed: ArrowUpDown,
};

interface CallOffDetailTabProps {
  callOff: CallOff;
  timeline: CallOffTimelineEvent[];
  editHistory: CallOffEditHistoryEntry[];
  formatDate: (date: string | Date) => string;
  formatDateTime: (date: string | Date) => string;
  /** CR-7732: the viewer is the customer, who may change the haulier. */
  isCustomer: boolean;
  /** CR-7732: reload the call-off and its history after a haulier change. */
  onRefresh: () => void;
  /** CR-7732: the viewer carries this load rather than trading it. */
  isHaulier: boolean;
  /** CR-7781: the Haulage block gained or lost unsaved edits. */
  onHaulageDirtyChange?: (dirty: boolean) => void;
}

/**
 * Map a lifecycle event name to the timeline icon variant.
 */
function eventType(event: string): TimelineItem['type'] {
  const e = event.toLowerCase();
  if (e === 'created') return 'create';
  if (e === 'cancelled' || e === 'declined') return 'decline';
  if (e === 'confirmed' || e === 'delivered') return 'approve';
  return 'update';
}

/**
 * Convert call-off lifecycle events + line edit entries into a
 * single chronologically-ordered TimelineItem array.
 */
function buildAudit(
  timeline: CallOffTimelineEvent[],
  editHistory: CallOffEditHistoryEntry[],
  formatDate: (date: string | Date) => string,
): TimelineItem[] {
  const items: TimelineItem[] = [];
  let key = 0;

  for (const ev of timeline) {
    items.push({
      id: ++key,
      timestamp: ev.timestamp,
      user: ev.user || 'System',
      userRole: ev.company,
      action: TIMELINE_EVENT_LABELS[ev.event]
        ?? ev.event.charAt(0).toUpperCase() + ev.event.slice(1).replace(/_/g, ' '),
      type: eventType(ev.event),
    });
  }

  for (const entry of editHistory) {
    const userLabel = entry.edited_by_name || 'System';
    const role = entry.edited_by_company_name;
    const reason = entry.delivery_name ? `Delivery: ${entry.delivery_name}` : undefined;

    // CR-7732: the haulier's own figures. One entry per save, listing what it
    // set — "Scat entered price £450" is on the record with a name and a
    // timestamp, without four values becoming four rows of history.
    // CR-7781: the values are what it set, not why, so they go in `detail` —
    // under `reason` the timeline labelled them "Reason:".
    if (entry.event_type === 'haulage_entered') {
      const haulage = haulageEntryLine(entry, formatDate);
      if (haulage) {
        items.push({
          id: ++key,
          timestamp: entry.created_at,
          user: userLabel,
          userRole: role,
          action: haulage.action,
          detail: haulage.detail,
          type: 'update',
        });
      }
      continue;
    }

    // CR-7781: the haulier's Confirm Load — Logistics Confirmed.
    if (entry.event_type === 'logistics_confirmed') {
      items.push({
        id: ++key,
        timestamp: entry.created_at,
        user: userLabel,
        userRole: role,
        action: 'Logistics Confirmed',
        type: 'approve',
      });
      continue;
    }

    // CR-7781: a cost the customer changed itself — CR-8613: or the lorry
    // number. One field, so it takes the timeline's Previous / New boxes and
    // its Reason line, as a quantity change does. The supplier is not sent the
    // change, so it shows nothing.
    if (entry.event_type === 'customer_haulage_cost') {
      const change = (entry.field_changes || []).find(c => c.field in CUSTOMER_HAULAGE_LABELS);
      if (change) {
        const isDate = change.field.endsWith('date');
        const shown = (v: string | null | undefined) => (v ? (isDate ? formatDate(v) : v) : undefined);
        items.push({
          id: ++key,
          timestamp: entry.created_at,
          user: userLabel,
          userRole: role,
          action: customerCostAction(change),
          field: CUSTOMER_HAULAGE_LABELS[change.field],
          oldValue: shown(change.old),
          newValue: shown(change.new),
          reason: change.reason || undefined,
          type: 'update',
        });
      }
      continue;
    }

    // CR-7732: a header event carries its values in field_changes, not items_*.
    if (entry.event_type === 'haulier_changed') {
      const change = (entry.field_changes || []).find(c => c.field === 'haulier');
      items.push({
        id: ++key,
        timestamp: entry.created_at,
        user: userLabel,
        userRole: role,
        action: haulierChangeAction(change),
        // The previous haulier's cleared entries — a record, not a reason.
        detail: haulierPreviousEntries(entry, formatDate),
        type: 'update',
      });
      continue;
    }

    for (const item of entry.items_added as EditHistoryItem[]) {
      items.push({
        id: ++key,
        timestamp: entry.created_at,
        user: userLabel,
        userRole: role,
        action: `Added ${item.part_number}${item.requested_qty ? ` (qty ${item.requested_qty})` : ''}`,
        reason,
        type: 'create',
      });
    }
    for (const item of entry.items_removed as EditHistoryItem[]) {
      items.push({
        id: ++key,
        timestamp: entry.created_at,
        user: userLabel,
        userRole: role,
        action: `Removed ${item.part_number}`,
        reason,
        type: 'decline',
      });
    }
    for (const item of entry.items_changed as EditHistoryChangedItem[]) {
      // CR-7744: a priority tick or untick, not a quantity change.
      if (item.field === 'is_priority') {
        const marked = item.new_qty === 'true';
        items.push({
          id: ++key,
          timestamp: entry.created_at,
          user: userLabel,
          userRole: role,
          action: marked
            ? `Marked ${item.part_number} as priority`
            : `Removed priority from ${item.part_number}`,
          reason: item.destination ? `Delivery: ${item.destination}` : reason,
          type: 'update',
        });
        continue;
      }
      items.push({
        id: ++key,
        timestamp: entry.created_at,
        user: userLabel,
        userRole: role,
        action: `Changed quantity for ${item.part_number}`,
        field: item.field || 'quantity',
        oldValue: item.old_qty,
        newValue: item.new_qty,
        reason,
        type: 'update',
      });
    }
  }

  // newest first
  items.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  return items;
}

function ReadOnlyField({ label, value, emphasis = false }: { label: string; value: string; emphasis?: boolean }) {
  return (
    <div>
      <label className={`block text-sm font-medium mb-1 ${emphasis ? 'text-primary' : 'text-muted-foreground'}`}>
        {label}
      </label>
      <input
        type="text"
        value={value || '-'}
        readOnly
        className="w-full px-3 py-2 border border-border rounded-lg bg-muted/30 font-medium"
      />
    </div>
  );
}

function DateField({ label, value, formatDate }: { label: string; value: string | null; formatDate: (d: string | Date) => string }) {
  return (
    <div>
      <label className="block text-sm font-medium text-muted-foreground mb-1">{label}</label>
      <div className="flex items-center gap-2 border border-border rounded-lg bg-muted/30 px-3 py-2">
        <Calendar className="h-4 w-4 text-muted-foreground" />
        <input
          type="text"
          value={value ? formatDate(value) : '-'}
          readOnly
          className="flex-1 bg-transparent border-none outline-none font-medium"
        />
      </div>
    </div>
  );
}

export default function CallOffDetailTab({
  callOff, timeline, editHistory, formatDate, formatDateTime: _formatDateTime,
  isCustomer, onRefresh, isHaulier: _isHaulier, onHaulageDirtyChange,
}: CallOffDetailTabProps) {
  const auditItems = buildAudit(timeline, editHistory, formatDate);
  const fillPercent = parseFloat(callOff.fill_percent) || 0;
  const DestIcon = DESTINATION_ICONS[callOff.destination_type];
  const isMixed = callOff.destination_type === 'mixed';
  const destLabel = isMixed
    ? (callOff.destination_name?.replace(/^Mixed:\s*/, '') || 'Mixed Delivery')
    : (callOff.destination_name || callOff.destination_type);

  // CR-6173: total whole pallets the haulier needs to book across all lines.
  // Each line rounds up independently (a part-filled pallet still takes a full
  // pallet space); direct lines carry a catalog-sourced pallet_qty from the API.
  // CR-7714: a line with no pallet quantity is counted and named, not added as 0.
  const totalPallets = describeTotal(palletTotal(callOff.lines || []), 'pallet data');

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Left column — fields */}
      <div className="card p-4 border rounded-lg">
        <div className="space-y-4">
          {/* Reference + Status row */}
          <div className="grid grid-cols-2 gap-3">
            <ReadOnlyField label="Reference" value={callOff.reference} emphasis />
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1">Status</label>
              <div className="flex items-center gap-2 border border-border rounded-lg bg-muted/30 px-3 py-2 h-[42px]">
                <CallOffStatusBadges
                  status={callOff.detail_status}
                  logisticsConfirmedAt={callOff.logistics_confirmed_at}
                />
              </div>
            </div>
          </div>

          {/* Parties */}
          <div className="grid grid-cols-2 gap-3">
            <ReadOnlyField label="Customer" value={callOff.customer_name} />
            <ReadOnlyField label="Supplier" value={callOff.supplier_name} />
          </div>

          {/* CR-7732: the haulier collecting this load */}
          <div className="grid grid-cols-2 gap-3">
            <CallOffHaulierField callOff={callOff} canEdit={isCustomer} onChanged={onRefresh} />
          </div>

          {/* CR-7732: its cost, dates and lorry. Renders nothing for the
              supplier, which is not sent these fields. */}
          <CallOffHaulagePanel
            callOff={callOff}
            onSaved={onRefresh}
            onDirtyChange={onHaulageDirtyChange}
          />

          {/* Dates */}
          <div className="grid grid-cols-2 gap-3">
            {/* CR-7782: the date the customer set. "Pickup Date" is the
                haulier's own, in the haulage panel and on the list. */}
            {/* CR-8613: changeable in place behind the customer's switch —
                Edit Call-Off, the other way to change it, closes at Dispatched. */}
            {callOff.can_customer_edit_haulage_cost ? (
              <CallOffCustomerHaulageCost field="pickup_date" callOff={callOff} onSaved={onRefresh} />
            ) : (
              <DateField label="Expected Pickup Date" value={callOff.pickup_date} formatDate={formatDate} />
            )}
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1">Destination</label>
              <div className="flex items-center gap-2 border border-border rounded-lg bg-muted/30 px-3 py-2">
                {DestIcon && <DestIcon className="h-4 w-4 text-muted-foreground" />}
                <span className={`font-medium ${isMixed ? 'text-purple-700' : ''}`}>{destLabel || '-'}</span>
              </div>
            </div>
          </div>

          {/* Fill % */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">Load Fill</label>
            <div className="flex items-center gap-3 border border-border rounded-lg bg-muted/30 px-3 py-2">
              <Progress value={Math.min(fillPercent, 100)} className="h-2 flex-1" />
              <span className={`text-sm font-bold whitespace-nowrap ${fillPercent > 90 ? 'text-red-600' : fillPercent > 50 ? 'text-orange-600' : 'text-green-600'}`}>
                {callOff.fill_percent}%
              </span>
            </div>
          </div>

          {/* Totals — CR-6173 adds Total Pallets (whole pallets to book). */}
          <div className="grid grid-cols-2 gap-3">
            <ReadOnlyField label="Total Items" value={String(callOff.total_items)} />
            <ReadOnlyField label="Total Requested" value={callOff.total_requested_qty} />
            <ReadOnlyField label="Total Confirmed" value={callOff.total_confirmed_qty} />
            <ReadOnlyField label="Total Pallets" value={totalPallets} emphasis />
          </div>

          {/* Lifecycle audit row — created / sent / confirmed / dispatched / delivered */}
          <div className="grid grid-cols-2 gap-3">
            <ReadOnlyField label="Created By" value={callOff.created_by_name} />
            <DateField label="Created At" value={callOff.created_at} formatDate={formatDate} />
          </div>
          {callOff.sent_at && (
            <div className="grid grid-cols-2 gap-3">
              <ReadOnlyField label="Sent By" value={callOff.sent_by_name} />
              <DateField label="Sent At" value={callOff.sent_at} formatDate={formatDate} />
            </div>
          )}
          {callOff.confirmed_at && (
            <div className="grid grid-cols-2 gap-3">
              {/* CR-7781: the supplier's confirmation, named as such beside the
                  haulier's Logistics Confirmed. */}
              <ReadOnlyField label="Supplier Confirmed By" value={callOff.confirmed_by_name} />
              <DateField label="Supplier Confirmed At" value={callOff.confirmed_at} formatDate={formatDate} />
            </div>
          )}
          {callOff.dispatched_at && (
            <div className="grid grid-cols-2 gap-3">
              <ReadOnlyField label="Dispatched By" value={callOff.dispatched_by_name} />
              <DateField label="Dispatched At" value={callOff.dispatched_at} formatDate={formatDate} />
            </div>
          )}
          {callOff.delivered_at && (
            <DateField label="Delivered At" value={callOff.delivered_at} formatDate={formatDate} />
          )}
          {callOff.cancelled_at && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <ReadOnlyField label="Cancelled By" value={callOff.cancelled_by_name} />
                <DateField label="Cancelled At" value={callOff.cancelled_at} formatDate={formatDate} />
              </div>
              {callOff.cancellation_reason && (
                <div>
                  <label className="block text-sm font-medium text-red-700 mb-1">Cancellation Reason</label>
                  <div className="border border-red-200 rounded-lg bg-red-50 px-3 py-2 font-medium text-red-900 whitespace-pre-wrap">
                    {callOff.cancellation_reason}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Notes */}
          {callOff.notes && (
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1 flex items-center gap-1">
                <FileText className="h-3.5 w-3.5" />
                Notes
              </label>
              <div className="w-full px-3 py-2 border border-border rounded-lg bg-muted/30 text-sm whitespace-pre-wrap">
                {callOff.notes}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right column — audit history */}
      <div className="border rounded-lg p-4">
        <div className="flex items-center gap-2 mb-4">
          <User className="h-4 w-4 text-muted-foreground" />
          <h3 className="font-semibold">Audit History</h3>
        </div>
        {/* CR-7732: no scroll box of its own. The modal body already scrolls,
            so a second one nested inside it fought the first for the wheel and
            cut the entry at its boundary in half — which read as the history
            being truncated rather than merely scrolled. The column flows with
            the modal now: one scrollbar, and every entry whole. */}
        <div className="pr-2">
          {auditItems.length > 0 ? (
            <VerticalTimeline items={auditItems} />
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No audit history yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
